import React, { PureComponent, Fragment } from 'react'
import { bool, string, shape, object, func, arrayOf } from 'prop-types'
import track from 'react-tracking'
import BEMHelper from '@ynap/bem-helper'
import MultiSelect from '@ynap/multi-select'
import Checkbox from '@ynap/checkbox'
import { Notification } from '@ynap/notification'
import { myaccountPageData } from '../analytics'
import Skeleton from '@ynap/skeleton'
import { SimpleAsyncButton } from '@ynap/button'
import { CombinedSelect } from '@ynap/select'
import { parse } from 'query-string'
import { RadioButtonGroup, RadioButton } from '@ynap/radio-button'
import Flag from '@ynap/flag'

const bem = new BEMHelper('PreferenceCenter')

@track({})
class PreferenceCenter extends PureComponent {
    static propTypes = {
        messages: shape({
            languages: object,
            marketingPreferences: shape({
                title: func.isRequired,
                description: func.isRequired,
                saveButton: func.isRequired,
                error: func.isRequired,
                preferences: object.isRequired,
                preferredLanguage: func.isRequired,
            }),
        }).isRequired,
        getSizePreferences: func.isRequired,
        fetchUserPreferences: func.isRequired,
        fetchUser: func.isRequired,
        fetchCountries: func.isRequired,
        addUserPreference: func.isRequired,
        removeUserPreference: func.isRequired,
        updateSubscriptions: func.isRequired,
        modifyUserPreferenceSuccess: func.isRequired,
        modifyUserPreferenceFail: func.isRequired,
        isUpdating: bool.isRequired,
        didSucceed: bool.isRequired,
        didFail: bool.isRequired,
        preferredCountry: string,
        emailConsent: bool.isRequired,
        sizePreferences: arrayOf(
            shape({
                identifier: string,
                name: string,
                values: arrayOf(
                    shape({
                        id: string.isRequired,
                        selected: bool.isRequired,
                        label: string.isRequired,
                    })
                ),
            })
        ).isRequired,
        countries: object.isRequired,
        updateUserMarketingCountryLang: func,
        updateUserInfo: func,
    }

    state = {
        changeMarketingCountry: null,
        changeMarketingLanguage: null,
        changeGenderPreference: null,
        countriesOptionsData: [],
        changedPrefs: {},
        error: null,
        success: null,
        isDirty: false,
    }

    @track(
        myaccountPageData({
            name: 'marketing preferences',
            type: 'marketing preferences page',
            subCategory1: 'marketing preferences',
        })
    )
    componentDidMount() {
        this.props.getSizePreferences()
        this.props.fetchUserPreferences()
        this.props.fetchUser()
        this.props.fetchCountries()
        this.setState({ countriesOptionsData: this.createCountryOptionsData(this.props.countries) })
    }

    componentDidUpdate(prevProps) {
        if (JSON.stringify(prevProps.countries) !== JSON.stringify(this.props.countries)) {
            this.setState({ countriesOptionsData: this.createCountryOptionsData(this.props.countries) })
        }
    }

    onPrefsChanged(prefSet, newPrefs) {
        this.setState({
            changedPrefs: {
                ...this.state.changedPrefs,
                [prefSet.identifier]: newPrefs,
            },
            isDirty: true,
        })
    }

    saveChanges = () => {
        const {
            modifyUserPreferenceSuccess,
            modifyUserPreferenceFail,
            updateSubscriptions,
            updateUserMarketingCountryLang,
        } = this.props

        const { changedEmailConsent, changeMarketingCountry, changeMarketingLanguage, changeGenderPreference } =
            this.state

        this.setState({
            error: null,
            success: null,
            isDirty: false,
        })

        if (Object.prototype.hasOwnProperty.call(this.state, 'changedEmailConsent')) {
            const { accessKey, email } = parse(window.location.search)

            updateSubscriptions({ maMarketingEmailStandard: changedEmailConsent }, { accessKey, email })
        }

        if (changeMarketingCountry || changeMarketingLanguage || changeGenderPreference) {
            updateUserMarketingCountryLang({
                ...(changeMarketingLanguage && {
                    preferredLanguage: changeMarketingLanguage,
                }),
                ...(changeMarketingCountry && {
                    contextAttribute: [
                        {
                            attributeName: 'maPreferredCountry',
                            attributeValue: {
                                value: changeMarketingCountry,
                            },
                        },
                    ],
                }),
                ...(changeGenderPreference && {
                    gender: changeGenderPreference,
                }),
            })
        }

        const promises = []

        Object.entries(this.state.changedPrefs).forEach(([prefId, prefs]) => {
            const originalPrefs = this.props.sizePreferences.find((prefSet) => prefSet.name === prefId) || {
                values: [],
            }
            const originalPrefValues = originalPrefs.values.reduce((acc, pref) => {
                return { ...acc, [pref.id]: pref }
            }, {})

            const toAdd = []
            const toDelete = []

            prefs.forEach((pref) => {
                const newPrefValue = pref.selected
                const origPrefValue = originalPrefValues[pref.id].selected

                if (!origPrefValue && newPrefValue) {
                    toAdd.push(pref.id)
                } else if (origPrefValue && !newPrefValue) {
                    toDelete.push(pref.id)
                }
            })

            if (toAdd.length) {
                promises.push(this.props.addUserPreference(prefId, { values: toAdd.map((name) => ({ name })) }))
            }

            if (toDelete.length) {
                promises.push(this.props.removeUserPreference(prefId, toDelete))
            }
        })

        Promise.all(promises)
            .then(() => modifyUserPreferenceSuccess())
            .catch(() => modifyUserPreferenceFail())
    }

    onEmailConsentChanged = (ev) => {
        const consentGiven = ev.target.checked
        this.setState({
            changedEmailConsent: consentGiven,
            ...(!consentGiven && {
                changeMarketingCountry: null,
                changeMarketingLanguage: null,
            }),
            isDirty: true,
        })
    }

    createCountryOptionsData(countries) {
        if (!countries) {
            return []
        }

        const allCountriesKeys = Object.keys(countries)

        return allCountriesKeys.map((iso) => ({
            value: countries[iso].countryCode,
            key: countries[iso].countryCode,
            label: (
                <div className={bem('countryOptionContainer')}>
                    <Flag countryISO={countries[iso].countryCode} />
                    <span className={bem('flagLabel')}>{countries[iso].countryName}</span>
                </div>
            ),
            labelNative: countries[iso].countryName,
        }))
    }

    countryLanguagesOptions = () => {
        const currentCountry = this.state.changeMarketingCountry || this.props.preferredCountry
        const countryLanguages = (this.props.countries && this.props.countries[currentCountry]?.languages) || {}
        const languageKeys = Object.keys(countryLanguages).map((lang) => countryLanguages[lang].locale)
        const currentSetPreferredLanguage = this.state.changeMarketingLanguage || this.props.preferredLanguage

        if (languageKeys.length <= 1) {
            // Set marketingLanguage to country's default language
            this.setState({
                changeMarketingLanguage: languageKeys[0],
            })

            return null
        } else {
            this.setState({
                changeMarketingLanguage:
                    (languageKeys.includes(currentSetPreferredLanguage) && currentSetPreferredLanguage) ||
                    languageKeys[0],
            })
        }

        return (
            <>
                <h3 className={bem('langSelection')}>{this.props.messages.marketingPreferences.preferredLanguage()}</h3>
                <RadioButtonGroup
                    className={bem('preferredMALang')}
                    component={RadioButtonGroup}
                    name="preferredMALang"
                    id="preferredMALang"
                    value={this.state.changeMarketingLanguage}
                >
                    {languageKeys.map((languageAsLocale) => {
                        const langCode = languageAsLocale.split('_')[0]
                        return (
                            <RadioButton
                                key={languageAsLocale}
                                value={languageAsLocale}
                                label={this.props.messages.languages[langCode]?.()}
                                onChange={() =>
                                    this.setState({
                                        changeMarketingLanguage: languageAsLocale,
                                        isDirty: true,
                                    })
                                }
                            />
                        )
                    })}
                </RadioButtonGroup>
            </>
        )
    }

    genderMarketingOptions = () => {
        const { messages, user } = this.props
        const genderPreference = this.state.changeGenderPreference || user?.gender || 'Female'

        return (
            <RadioButtonGroup
                className={bem('preferredMAGender')}
                component={RadioButtonGroup}
                name="preferredMAGender"
                id="preferredMAGender"
                value={genderPreference}
            >
                <RadioButton
                    className={bem('preferredGenderRadioButton')}
                    key={messages.marketingPreferences.preferences.genderWomens()}
                    value={'Female'}
                    label={messages.marketingPreferences.preferences.genderWomens()}
                    onChange={(e) =>
                        this.setState({
                            changeGenderPreference: e.target.value,
                            isDirty: true,
                        })
                    }
                />
                <RadioButton
                    className={bem('preferredGenderRadioButton')}
                    key={messages.marketingPreferences.preferences.genderMens()}
                    value={'Male'}
                    label={messages.marketingPreferences.preferences.genderMens()}
                    onChange={(e) =>
                        this.setState({
                            changeGenderPreference: e.target.value,
                            isDirty: true,
                        })
                    }
                />
                <RadioButton
                    className={bem('preferredGenderRadioButton')}
                    key={messages.marketingPreferences.preferences.genderAll()}
                    value={'Unspecified'}
                    label={messages.marketingPreferences.preferences.genderAll()}
                    onChange={(e) =>
                        this.setState({
                            changeGenderPreference: e.target.value,
                            isDirty: true,
                        })
                    }
                />
            </RadioButtonGroup>
        )
    }

    sizePreferenceOptions = () => {
        const { sizePreferences, messages } = this.props
        const { changedPrefs } = this.state

        return (
            sizePreferences.map &&
            sizePreferences.map((prefSet) => {
                const preferenceMsgs = messages.marketingPreferences.preferences[prefSet.identifier]
                const title = preferenceMsgs ? preferenceMsgs.title() : prefSet.name
                const options = changedPrefs[prefSet.identifier] || prefSet.values
                const shoePreference = prefSet.identifier === 'SHOESIZE'

                return (
                    <>
                        <div className={bem('preference')} key={prefSet.identifier}>
                            <h3 className={bem('preferenceName')}>{title}</h3>
                            {preferenceMsgs && (
                                <p className={bem('preferenceDescription')}>{preferenceMsgs.description()}</p>
                            )}

                            <MultiSelect
                                idPrefix={prefSet.identifier}
                                options={options}
                                onChange={(newPrefs) => this.onPrefsChanged(prefSet, newPrefs)}
                            />
                            {shoePreference && <p className={bem('sizeSchema')}>{preferenceMsgs.sizeSchema()}</p>}
                        </div>
                    </>
                )
            })
        )
    }

    render() {
        const { sizePreferences, messages, isUpdating, didSucceed, didFail, preferredCountry, emailConsent, config } =
            this.props
        const { fullPageHeader } = config?.head?.flags || {}

        const { changedEmailConsent, isDirty } = this.state

        const emailConsentHasChanged = Object.prototype.hasOwnProperty.call(this.state, 'changedEmailConsent')
        const isConsentingToEmailMarketing = emailConsentHasChanged ? changedEmailConsent : emailConsent
        const loading = sizePreferences?.length < 1 && !didFail

        return (
            <div className={bem()}>
                {!fullPageHeader && <h2 className={bem('title')}>{messages.marketingPreferences.title()}</h2>}
                <p className={bem('description')}>{messages.marketingPreferences.description()}</p>
                <div className={bem('preference', 'marketingPreference')}>
                    <h3 className={bem('preferenceName')}>
                        {messages.marketingPreferences.preferences.marketingConsent.title()}
                    </h3>
                    <p className={bem('preferenceDescription')}>
                        {messages.marketingPreferences.preferences.marketingConsent.description()}
                    </p>

                    <Checkbox
                        className={bem('checkbox')}
                        checked={isConsentingToEmailMarketing}
                        onChange={this.onEmailConsentChanged}
                        id="emailconsent"
                    >
                        {messages.marketingPreferences.preferences.marketingConsent.checkboxLabel()}
                    </Checkbox>
                    {isConsentingToEmailMarketing && (
                        <>
                            {this.genderMarketingOptions()}
                            <h3 className={bem('countrySelectionLabel')}>
                                {messages.marketingPreferences.preferredCountry()}
                            </h3>
                            <CombinedSelect
                                type="defaultLink"
                                className={bem('countrySelection')}
                                value={this.state.changeMarketingCountry || preferredCountry}
                                onChange={(value) => this.setState({ changeMarketingCountry: value, isDirty: true })}
                                options={this.state.countriesOptionsData}
                                emptyLabel={messages.marketingPreferences.emptyLabel()}
                            />
                            {this.countryLanguagesOptions()}
                        </>
                    )}
                </div>

                {!loading ? (
                    <>
                        <div className={bem('preference')}>
                            <h2 className={bem('preferenceSubtitle')}>
                                {messages.marketingPreferences.preferences.genderWomens()}
                            </h2>
                        </div>
                        {this.sizePreferenceOptions()}
                    </>
                ) : (
                    <Fragment>
                        <div className={bem('skeletonOne')}>
                            <Skeleton count={2} align={'left'} />
                        </div>
                        <div className={bem('skeletonTwo')}>
                            <Skeleton count={2} align={'left'} />
                        </div>
                    </Fragment>
                )}
                {didSucceed && (
                    <Notification
                        type={'success'}
                        className={bem('notification', (isUpdating || isDirty) && 'hide')}
                        message={messages.marketingPreferences.notification()}
                    />
                )}
                {didFail && (
                    <Notification
                        type={'error'}
                        className={bem('notification', (isUpdating || isDirty) && 'hide')}
                        message={messages.marketingPreferences.error()}
                    />
                )}
                <SimpleAsyncButton
                    className={bem('saveButton')}
                    onClick={this.saveChanges}
                    disabled={!isDirty}
                    loading={isUpdating}
                    copy={messages.marketingPreferences.saveButton()}
                />
            </div>
        )
    }
}

export default PreferenceCenter
